import { Box, Button, Checkbox, Stack, Typography, useTheme } from '@mui/material';
import React, { useId } from 'react';

import { ColumnCheckItem } from './ColumnCheckItem';
import { groupCheckState, GroupView } from './columnSelectionHelpers';

export interface ColumnGroupSectionProps {
  view: GroupView;
  /** True when a search or the selected-only toggle is narrowing the rows. */
  isFiltered: boolean;
  /** Show the group name — redundant when the sidebar has already named it. */
  showHeading: boolean;
  onToggleField: (groupIndex: number, field: string, checked: boolean) => void;
  onSetVisible: (view: GroupView, checked: boolean) => void;
}

export const ColumnGroupSection: React.FC<ColumnGroupSectionProps> = ({
  view,
  isFiltered,
  showHeading,
  onToggleField,
  onSetVisible,
}) => {
  const theme = useTheme();
  const headingId = useId();
  const state = groupCheckState(view);

  // While filtering, bulk actions reach only the rows on screen — so say so in
  // the label rather than leaving "Select all" to mean two different things.
  const scope = isFiltered ? `${view.items.length} shown` : 'all';

  return (
    <Box component="section" role="group" aria-labelledby={headingId} sx={{ pb: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          px: 2,
          py: 0.75,
          bgcolor: theme.palette.v4.surface,
          borderBottom: `1px solid ${theme.palette.v4.border}`,
        }}
      >
        <Checkbox
          size="small"
          checked={state === 'all'}
          indeterminate={state === 'some'}
          onChange={() => onSetVisible(view, state !== 'all')}
          inputProps={{
            'aria-label': `Select ${scope} columns in ${view.title}`,
          }}
          sx={{ p: 0.5, color: theme.palette.v4.borderDark }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {showHeading && (
            <Typography id={headingId} variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {view.title}
            </Typography>
          )}
          <Typography
            id={showHeading ? undefined : headingId}
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {showHeading
              ? `${view.selected} of ${view.items.length} selected`
              : `${view.title} — ${view.selected} of ${view.items.length} selected`}
            {view.hidden > 0 && ` · ${view.hidden} hidden by search`}
          </Typography>
        </Box>

        <Button
          size="small"
          onClick={() => onSetVisible(view, true)}
          sx={{ minWidth: 0, color: theme.palette.v4.accentDark, textTransform: 'none' }}
        >
          {isFiltered ? `Select ${view.items.length} shown` : 'Select all'}
        </Button>
        <Typography variant="caption" sx={{ color: theme.palette.v4.borderDark }}>
          |
        </Typography>
        <Button
          size="small"
          onClick={() => onSetVisible(view, false)}
          sx={{ minWidth: 0, color: theme.palette.v4.accentDark, textTransform: 'none' }}
        >
          {isFiltered ? `Deselect ${view.items.length} shown` : 'Deselect all'}
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          // Track count follows the dialog's own width rather than the
          // viewport's, so a group reflows correctly whether or not the
          // sidebar is taking 176px off the pane.
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          columnGap: 1,
          px: 1,
          pt: 0.5,
        }}
      >
        {view.items.map((item) => (
          <ColumnCheckItem
            // Fields repeat across groups, so the key has to be group-scoped.
            key={`${view.index}::${item.field}`}
            label={item.title}
            checked={item.value}
            onChange={(checked) => onToggleField(view.index, item.field, checked)}
          />
        ))}
      </Box>
    </Box>
  );
};
